/**
 * Utility function to pause execution for a specified duration
 * @param ms Duration to sleep in milliseconds
 * @returns Promise that resolves after the specified duration
 */
export const sleep = (ms: number): Promise<void> => 
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * Utility function to retry an async operation with exponential backoff
 * @param fn Function to retry
 * @param attempts Number of retries
 * @param delay Initial delay in milliseconds
 * @param shouldRetry Optional function to determine if an error should trigger a retry
 * @returns Promise that resolves with the operation result
 */
export const retry = async <T>(
  fn: () => Promise<T>,
  attempts: number = 3,
  delay: number = 1000,
  shouldRetry: (error: any) => boolean = (error) => {
    // Retry on network errors, timeouts, and server errors
    if (error instanceof Error && error.message === 'Failed to fetch') {
      return true;
    }
    if (error instanceof DOMException && error.name === 'AbortError') {
      return true;
    }
    if (error instanceof Error && error.message.includes('Server error:')) {
      return true;
    }
    if (error instanceof Error && error.message.includes('NetworkError')) {
      return true;
    }
    if (error instanceof Error && error.message.includes('timeout')) {
      return true;
    }
    // Retry on specific HTTP status codes
    if (error.status >= 500 || error.status === 429 || error.status === 408) {
      return true;
    }
    return false;
  }
): Promise<T> => {
  let lastError: Error;
  
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      // Don't retry if the error shouldn't be retried
      if (!shouldRetry(error)) {
        throw lastError;
      }

      // Don't wait on the last attempt
      if (i < attempts - 1) {
        // Exponential backoff with jitter
        const backoff = Math.min(
          delay * Math.pow(2, i) * (0.5 + Math.random() * 0.5),
          30000 // Cap at 30 seconds
        );
        await sleep(backoff);
      }
    }
  }

  throw lastError!;
};